/**
 * Чистое ядро фильтров, сортировки и счётчиков (§10.1–10.3, §4.5).
 *
 * Здесь нет React, стора и обращений к сети: вход — массив брендов и описание
 * фильтра, выход — массив брендов и числа. Это сделано ради трёх вещей:
 * счётчики считаются ровно той же функцией, что и набор строк; поведение
 * проверяется без интерфейса; одна и та же алгебра работает и в таблице, и в
 * представлениях.
 *
 * ── Алгебра (§10.1), выписана словами в спеке и реализована буквально ───────
 *  · внутри одной группы — ИЛИ: `Тир: A+ или A`;
 *  · между группами — И: `(Тир: A+ или A) и (Без URL)`;
 *  · каждый флаг — своя группа, поэтому «Без URL И категории не уточнены» —
 *    это два независимых условия;
 *  · исключение (Alt+клик) вычитает: бренд со исключённым значением выпадает
 *    даже если он подходит по включённым значениям той же группы. Для групп
 *    значений исключения лежат в `exclude`, для флагов-тумблеров роль
 *    исключения играет обратное значение самого флага (`hasUrl: true` —
 *    это «− Без URL»);
 *  · «Доделки» и «Мои пропуски» — ОДНА группа с внутренним ИЛИ, а не два
 *    фильтра: иначе их пришлось бы выражать «И», и представление «Доделки»
 *    (§10.4: без URL ИЛИ категории не уточнены) стало бы невыразимым.
 *
 * ── Вселенные (П7, §10.2) ──────────────────────────────────────────────────
 * Канальные признаки (тир, URL, видимость, SEO, контекст, статусы, примечание,
 * White Glove) читаются из активного канала; брендовые (категории, флаг
 * «не уточнены», комментарии, дата и автор изменения, архив) — из бренда.
 *
 * ── Опубликованные значения (§5.4, П2) ─────────────────────────────────────
 * По умолчанию всё считается по ОПУБЛИКОВАННЫМ значениям: правка в черновике
 * не выбрасывает строку из-под курсора и не двигает счётчики. Передача
 * `ctx.overlay` переключает чтение на черновые значения — это нужно ровно для
 * одного: посчитать строки, которые после «Обновить» уйдут из фильтра
 * (строка «N строк больше не подходят под фильтр»).
 */
import type {
  Brand, BrandFilters as PublishedBrandFilters, ChannelCode, EnumSetCode,
  FieldDefinition, SortRule,
} from '@shared/types.ts'
import { channelCell, type Overlay } from '../../../lib/draft-overlay.js'

// ───────────────────────────── модель фильтра ────────────────────────────────

/** Значение «не задано» в группах значений. Пустая строка не бывает кодом справочника. */
export const UNSET = ''

/** Поля канала, у которых есть значение и которые сравниваются между каналами. */
export const CHANNEL_FIELDS = [
  'tier', 'visibility', 'url', 'stockDisplay', 'oos',
  'delivery', 'installation', 'warranty', 'seoPriority', 'contextPriority',
] as const
export type ChannelField = (typeof CHANNEL_FIELDS)[number]

export type WhiteGloveField = 'delivery' | 'installation' | 'warranty'

/** Группы значений, из которых можно исключать (Alt+клик по чипу). */
export interface FilterExclusions {
  tiers?: (string | null)[]
  visibility?: string[]
  seoPriority?: string[]
  contextPriority?: string[]
  stockDisplay?: string[]
  oos?: string[]
  categoryIds?: number[]
  changedBy?: number[]
  commentBy?: number[]
}

/**
 * Фильтры экрана. Расширяет контракт `shared/types.ts` полями, которых требует
 * §10.2 и которых в контракте нет; расширение остаётся совместимым с
 * `ViewConfig.filters`, поэтому представления сохраняются как есть.
 */
export interface BrandFilters extends PublishedBrandFilters {
  /** `any` — любая из выбранных категорий, `all` — все выбранные (§10.2). */
  categoriesMode?: 'any' | 'all'
  /** White Glove заполнен: любое из выбранных полей непусто. */
  whiteGlove?: WhiteGloveField[]
  /** Есть примечание в активном канале. */
  /** Строгие расхождения каналов: оба значения заполнены и различны (§4.6). */
  channelDiff?: boolean
  /** Заполнено только в одном канале. */
  onlyOneChannel?: boolean
  /** Только бренды с моими правками (чип появляется при непустом черновике). */
  onlyMine?: boolean
  /** Линза «изменилось с прошлого визита». */
  lensVisit?: boolean
  /** Доделки: без URL ИЛИ категории не уточнены — одна группа (§10.4). */
  todo?: boolean
  /** Мои пропуски: пусто хотя бы одно поле зоны ответственности (§4.9). */
  myMissing?: boolean
  /* ── комментарии и задачи ──
     По ним ведут работу: «что писал Дмитрий», «где давно не писали»,
     «где висит незакрытая задача». */
  /** Авторы последнего комментария. */
  commentBy?: number[]
  /** Последний комментарий не старше N дней. */
  commentWithinDays?: number
  /** У бренда есть незакрытая задача. */
  hasTask?: boolean
  exclude?: FilterExclusions
}

/** Что ядру нужно знать помимо самих брендов. Всё необязательное. */
export interface FilterContext {
  /** Определения пользовательских полей — для фильтров и сортировки по ним. */
  fields?: readonly FieldDefinition[]
  /** Бренды с моими правками в черновике — чип «Только мои правки». */
  draftBrandIds?: ReadonlySet<number>
  /** Бренды, созданные в черновике: показываются вне фильтра (§4.3). */
  newBrandIds?: ReadonlySet<number>
  /** Бренды, изменившиеся с прошлого визита — линза (§8.1). */
  visitChangedIds?: ReadonlySet<number>
  /** Порядок значений справочников: код набора → значение → sortOrder (§10.3). */
  enumOrder?: ReadonlyMap<string, ReadonlyMap<string, number>>
  /** Поля зоны ответственности смотрящего (§4.9): «Мои пропуски», сортировка. */
  areaFields?: readonly string[]
  /** Наложение черновика: читать черновые значения вместо опубликованных (§5.4). */
  overlay?: Overlay
}

/** Набор справочника для канального поля: порядок сортировки берётся из него. */
const ENUM_SET: Partial<Record<ChannelField, EnumSetCode>> = {
  tier: 'tier',
  visibility: 'visibility',
  stockDisplay: 'stock_display',
  oos: 'oos',
  seoPriority: 'seo_priority',
  contextPriority: 'context_priority',
}

// ───────────────────────────── чтение значений ───────────────────────────────

const collator = new Intl.Collator('ru', { sensitivity: 'base', numeric: true })

/**
 * Порядок названий брендов: сначала латиница, потом кириллица.
 *
 * Русская локаль в ICU ставит кириллицу ПЕРЕД латиницей, и тогда список
 * открывается на «Атлант», а привычные Asko и Bosch уезжают вниз. В исходной
 * Google-таблице порядок обратный (AC ELECTRIC, AEG, Alveus… а русские марки
 * в конце), и люди помнят список именно таким — сохраняем это.
 */
function scriptBucket(name: string): number {
  const first = name.trim().charAt(0).toLowerCase()
  if (first >= 'а' && first <= 'я') return 1
  if (first === 'ё') return 1
  return 0
}

/** Сравнение названий брендов с учётом порядка алфавитов. */
export function compareNames(a: string, b: string): number {
  const bucket = scriptBucket(a) - scriptBucket(b)
  return bucket !== 0 ? bucket : collator.compare(a, b)
}

/** Пустая строка и пробелы — это «пусто», а не значение (П3). */
function text(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/** Значение канального поля: по умолчанию опубликованное, с overlay — черновое. */
function channelValue(
  brand: Brand, channel: ChannelCode, field: ChannelField, ctx?: FilterContext,
): string | null {
  if (ctx?.overlay) return text(channelCell(ctx.overlay, brand, channel, field).value)
  const values = brand.channels[channel]
  if (!values) return null
  return text((values as unknown as Record<string, unknown>)[field])
}

/** Ведётся ли бренд в канале. Присутствие правится как обычное поле канала. */
function isPresent(brand: Brand, channel: ChannelCode, ctx?: FilterContext): boolean {
  if (ctx?.overlay) return channelCell(ctx.overlay, brand, channel, 'isPresent').value === true
  return brand.channels[channel]?.isPresent === true
}

const OTHER: Record<ChannelCode, ChannelCode> = { retail: 'b2b', b2b: 'retail' }

/** Привязки категорий, которые видят все: черновые в фильтре не участвуют (П2). */
function publishedCategories(brand: Brand): Brand['categories'] {
  return brand.categories.filter((link) => !link.isDraft)
}

/** Когда бренд менялся последний раз: бренд или любой его канал. */
export function lastChangedAt(brand: Brand): number {
  let max = Date.parse(brand.updatedAt)
  if (Number.isNaN(max)) max = 0
  for (const code of ['retail', 'b2b'] as const) {
    const at = brand.channels[code]?.updatedAt
    if (!at) continue
    const parsed = Date.parse(at)
    if (!Number.isNaN(parsed) && parsed > max) max = parsed
  }
  return max
}

/** Идентификатор системного автора «Импорт из Google-таблицы» (updatedBy = null). */
export const SYSTEM_AUTHOR_ID = 0

/** Автор последнего изменения бренда: тот, чья правка самая свежая (§10.2). */
export function lastEditorId(brand: Brand): number {
  let at = Date.parse(brand.updatedAt)
  if (Number.isNaN(at)) at = 0
  let who = brand.updatedBy
  for (const code of ['retail', 'b2b'] as const) {
    const values = brand.channels[code]
    if (!values?.updatedAt) continue
    const parsed = Date.parse(values.updatedAt)
    if (Number.isNaN(parsed) || parsed < at) continue
    at = parsed
    who = values.updatedBy
  }
  return who ?? SYSTEM_AUTHOR_ID
}

// ───────────────────────────── поиск по названию ─────────────────────────────

/** Транслитерация кириллицы в латиницу: «аско» и «Asko» должны сойтись (§10.2). */
const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i',
  й: 'i', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't',
  у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y',
  ь: '', э: 'e', ю: 'yu', я: 'ya',
}

/**
 * Скелет строки: нижний регистр, без знаков, кириллица в латинице, w = v.
 * Диакритика снимается: в данных есть «ZÜGEL», и его должно находить «zugel».
 */
function skeleton(value: string): string {
  let out = ''
  const flat = value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  for (const char of flat) {
    const mapped = TRANSLIT[char]
    if (mapped !== undefined) out += mapped
    else if (/[0-9a-z]/.test(char)) out += char === 'w' ? 'v' : char
  }
  return out
}

/**
 * Нечёткое совпадение: символы запроса идут в названии по порядку, но
 * растягиваться им нельзя. Без ограничения на длину совпадения «bosh» ловит
 * «Bone Crusher» и «Белоснежку» — такой поиск хуже отсутствующего.
 */
const FUZZY_SLACK = 2

function subsequence(haystack: string, needle: string): boolean {
  let index = 0
  let start = -1
  for (const char of needle) {
    index = haystack.indexOf(char, index)
    if (index < 0) return false
    if (start < 0) start = index
    index += 1
  }
  return index - start <= needle.length + FUZZY_SLACK
}

/** Подходит ли название под поисковый запрос: подстрока, транслит или нечётко. */
export function matchesQuery(name: string, query: string): boolean {
  const q = query.trim()
  if (q === '') return true
  if (name.toLowerCase().includes(q.toLowerCase())) return true
  const nameSk = skeleton(name)
  const qSk = skeleton(q)
  if (qSk === '') return true
  if (nameSk.includes(qSk)) return true
  /* Нечёткое — только от трёх символов: короткий запрос иначе подходит всем. */
  return qSk.length >= 3 && subsequence(nameSk, qSk)
}

/**
 * Границы подсветки совпадения в названии (§10.2: фон `--fresh-bg`).
 * Подсвечиваем только буквальное совпадение: транслит и нечёткий поиск
 * сопоставляют скелеты, и обратное отображение в исходные символы врало бы.
 */
export function queryMatchRange(name: string, query: string): [number, number] | null {
  const q = query.trim()
  if (q === '') return null
  const at = name.toLowerCase().indexOf(q.toLowerCase())
  return at < 0 ? null : [at, at + q.length]
}

// ───────────────────────────── предикаты групп ───────────────────────────────

function has<T>(list: readonly T[] | undefined): list is readonly T[] {
  return Array.isArray(list) && list.length > 0
}

/** Нормализация значения группы: null и '' — одно и то же «не задано». */
function key(value: string | null): string {
  return value ?? UNSET
}

/**
 * Группа значений: включения — ИЛИ, исключения вычитают.
 * `null` и `UNSET` в списке означают «не задано».
 */
function groupMatches(
  value: string | null,
  include: readonly (string | null)[] | undefined,
  exclude: readonly (string | null)[] | undefined,
): boolean {
  const current = key(value)
  if (has(exclude) && exclude.some((item) => key(item) === current)) return false
  if (!has(include)) return true
  return include.some((item) => key(item) === current)
}

/** Флаг-тумблер: `undefined` — выключен, иначе требуемое значение признака. */
function flagMatches(expected: boolean | undefined, actual: boolean): boolean {
  return expected === undefined || expected === actual
}

/** Строгое расхождение каналов: оба значения заполнены и различны (§4.6). */
export function hasStrictChannelDiff(brand: Brand, ctx?: FilterContext): boolean {
  if (!isPresent(brand, 'retail', ctx) || !isPresent(brand, 'b2b', ctx)) return false
  return CHANNEL_FIELDS.some((field) => {
    const a = channelValue(brand, 'retail', field, ctx)
    const b = channelValue(brand, 'b2b', field, ctx)
    return a !== null && b !== null && a !== b
  })
}

/** Заполнено только в одном канале: в одном значение есть, в другом пусто. */
export function filledInOneChannelOnly(brand: Brand, ctx?: FilterContext): boolean {
  if (!isPresent(brand, 'retail', ctx) || !isPresent(brand, 'b2b', ctx)) return false
  return CHANNEL_FIELDS.some((field) => {
    const a = channelValue(brand, 'retail', field, ctx)
    const b = channelValue(brand, 'b2b', field, ctx)
    return (a === null) !== (b === null)
  })
}

/** Сколько полей зоны ответственности пусто у бренда в канале (§4.9). */
export function missingCount(
  brand: Brand, channel: ChannelCode, ctx?: FilterContext,
): number {
  const fields = ctx?.areaFields
  if (!has(fields)) return 0
  let count = 0
  for (const field of fields) {
    if (field === 'categoriesUnclear') {
      if (brand.categoriesUnclear) count += 1
      continue
    }
    if (field === 'categories') {
      if (publishedCategories(brand).length === 0) count += 1
      continue
    }
    if ((CHANNEL_FIELDS as readonly string[]).includes(field)) {
      if (channelValue(brand, channel, field as ChannelField, ctx) === null) count += 1
    }
  }
  return count
}

// ───────────────────────────── пользовательские поля ─────────────────────────

export type FieldFilter =
  | { kind: 'select'; values: string[] }
  | { kind: 'user'; values: number[] }
  | { kind: 'number'; min?: number; max?: number }
  | { kind: 'date'; from?: string; to?: string }
  | { kind: 'bool'; value: boolean | null }
  | { kind: 'text'; filled: boolean }

/** Разбор значения из `filters.fields`: контракт хранит его как `unknown`. */
export function asFieldFilter(raw: unknown): FieldFilter | null {
  if (!raw || typeof raw !== 'object') return null
  const value = raw as Record<string, unknown>
  switch (value.kind) {
    case 'select':
      return Array.isArray(value.values)
        ? { kind: 'select', values: value.values.filter((v): v is string => typeof v === 'string') }
        : null
    case 'user':
      return Array.isArray(value.values)
        ? { kind: 'user', values: value.values.filter((v): v is number => typeof v === 'number') }
        : null
    case 'number': {
      const min = typeof value.min === 'number' ? value.min : undefined
      const max = typeof value.max === 'number' ? value.max : undefined
      return min === undefined && max === undefined ? null : { kind: 'number', min, max }
    }
    case 'date': {
      const from = typeof value.from === 'string' ? value.from : undefined
      const to = typeof value.to === 'string' ? value.to : undefined
      return from === undefined && to === undefined ? null : { kind: 'date', from, to }
    }
    case 'bool':
      return { kind: 'bool', value: typeof value.value === 'boolean' ? value.value : null }
    case 'text':
      return { kind: 'text', filled: value.filled !== false }
    default:
      return null
  }
}

/**
 * Значения пользовательского поля у бренда: у `brand_category` их столько,
 * сколько привязок, поэтому всегда массив — бренд подходит, если подходит
 * хотя бы одно значение (внутри группы ИЛИ).
 * Читается опубликованный слой: фильтры не смотрят в черновик (§5.4).
 */
function fieldValues(brand: Brand, def: FieldDefinition, channel: ChannelCode): unknown[] {
  if (def.scope === 'brand') return [brand.fields[def.code]]
  if (def.scope === 'brand_channel') return [brand.channels[channel]?.fields[def.code]]
  return publishedCategories(brand).map((link) => link.fields[def.code])
}

function filled(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim() !== ''
  if (Array.isArray(value)) return value.length > 0
  return true
}

function fieldValueMatches(value: unknown, filter: FieldFilter): boolean {
  switch (filter.kind) {
    case 'select': {
      if (filter.values.length === 0) return true
      const own = Array.isArray(value)
        ? value.filter((v): v is string => typeof v === 'string')
        : typeof value === 'string' ? [value] : []
      if (filter.values.includes(UNSET) && own.length === 0) return true
      return own.some((v) => filter.values.includes(v))
    }
    case 'user': {
      if (filter.values.length === 0) return true
      return typeof value === 'number' && filter.values.includes(value)
    }
    case 'number': {
      const num = typeof value === 'number' ? value : Number.NaN
      if (Number.isNaN(num)) return false
      if (filter.min !== undefined && num < filter.min) return false
      if (filter.max !== undefined && num > filter.max) return false
      return true
    }
    case 'date': {
      const at = typeof value === 'string' ? Date.parse(value) : Number.NaN
      if (Number.isNaN(at)) return false
      if (filter.from !== undefined) {
        const from = Date.parse(filter.from)
        if (!Number.isNaN(from) && at < from) return false
      }
      if (filter.to !== undefined) {
        const to = Date.parse(filter.to)
        /* Верхняя граница включительно: период задаётся датами, не моментами. */
        if (!Number.isNaN(to) && at > to + 86_399_999) return false
      }
      return true
    }
    case 'bool':
      return filter.value === null ? !filled(value) : value === filter.value
    case 'text':
      return filled(value) === filter.filled
  }
}

function customFieldsMatch(
  brand: Brand, filters: BrandFilters, channel: ChannelCode, ctx?: FilterContext,
): boolean {
  const raw = filters.fields
  if (!raw) return true
  const defs = ctx?.fields
  for (const [code, value] of Object.entries(raw)) {
    const filter = asFieldFilter(value)
    if (!filter) continue
    const def = defs?.find((item) => item.code === code)
    if (!def) continue
    const values = fieldValues(brand, def, channel)
    if (!values.some((item) => fieldValueMatches(item, filter))) return false
  }
  return true
}

// ───────────────────────────── applyFilters ──────────────────────────────────

/** Сколько групп фильтра включено — для подписи «⚗ Фильтры (N)» (§4.1). */
export function countActiveGroups(filters: BrandFilters): number {
  let n = 0
  const ex = filters.exclude
  if (has(filters.tiers) || has(ex?.tiers)) n += 1
  if (filters.presentIn !== undefined) n += 1
  if (has(filters.visibility) || has(ex?.visibility)) n += 1
  if (has(filters.categoryIds) || has(ex?.categoryIds)) n += 1
  if (has(filters.seoPriority) || has(ex?.seoPriority)) n += 1
  if (has(filters.contextPriority) || has(ex?.contextPriority)) n += 1
  if (has(filters.stockDisplay) || has(ex?.stockDisplay)) n += 1
  if (has(filters.oos) || has(ex?.oos)) n += 1
  if (filters.hasUrl !== undefined) n += 1
  if (filters.categoriesUnclear !== undefined) n += 1
  if (filters.hasComments !== undefined) n += 1
  if (filters.changedWithinDays !== undefined) n += 1
  if (has(filters.changedBy) || has(ex?.changedBy)) n += 1
  if (has(filters.commentBy)) n += 1
  if (filters.commentWithinDays !== undefined) n += 1
  if (filters.hasTask !== undefined) n += 1
  if (filters.status !== undefined && filters.status !== 'active') n += 1
  if (has(filters.whiteGlove)) n += 1
  if (filters.channelDiff !== undefined) n += 1
  if (filters.onlyOneChannel !== undefined) n += 1
  if (filters.onlyMine !== undefined) n += 1
  if (filters.lensVisit !== undefined) n += 1
  if (filters.todo !== undefined) n += 1
  if (filters.myMissing !== undefined) n += 1
  for (const value of Object.values(filters.fields ?? {})) {
    if (asFieldFilter(value)) n += 1
  }
  return n
}

/** Подходит ли бренд под статусную часть фильтра: вселенная счётчиков (П7). */
export function matchesStatus(brand: Brand, filters: BrandFilters): boolean {
  const status = filters.status ?? 'active'
  return status === 'all' || brand.status === status
}

/**
 * Один бренд против всего фильтра. Поиск по названию сюда входит: он такая же
 * группа, как остальные (§10.2).
 */
export function matchesFilters(
  brand: Brand, filters: BrandFilters, channel: ChannelCode, ctx?: FilterContext,
): boolean {
  if (!matchesStatus(brand, filters)) return false

  /* Один канал за раз (§4.6): в таблице только бренды активного канала.
     Это и есть вселенная «Показано N из 230». */
  if (!isPresent(brand, channel, ctx)) return false

  if (filters.query && !matchesQuery(brand.name, filters.query)) return false

  const ex = filters.exclude

  // ── канальные группы значений ──
  if (!groupMatches(channelValue(brand, channel, 'tier', ctx), filters.tiers, ex?.tiers)) return false
  if (!groupMatches(channelValue(brand, channel, 'visibility', ctx), filters.visibility, ex?.visibility)) return false
  if (!groupMatches(channelValue(brand, channel, 'seoPriority', ctx), filters.seoPriority, ex?.seoPriority)) return false
  if (!groupMatches(channelValue(brand, channel, 'contextPriority', ctx), filters.contextPriority, ex?.contextPriority)) return false
  if (!groupMatches(channelValue(brand, channel, 'stockDisplay', ctx), filters.stockDisplay, ex?.stockDisplay)) return false
  if (!groupMatches(channelValue(brand, channel, 'oos', ctx), filters.oos, ex?.oos)) return false

  // ── канальные флаги ──
  if (!flagMatches(filters.hasUrl, channelValue(brand, channel, 'url', ctx) !== null)) return false
  if (has(filters.whiteGlove)) {
    const any = filters.whiteGlove.some((field) => channelValue(brand, channel, field, ctx) !== null)
    if (!any) return false
  }

  // ── присутствие в каналах ──
  if (filters.presentIn !== undefined) {
    const here = isPresent(brand, 'retail', ctx)
    const there = isPresent(brand, 'b2b', ctx)
    const ok =
      filters.presentIn === 'retail' ? here
        : filters.presentIn === 'b2b' ? there
          : filters.presentIn === 'both' ? here && there
            : here !== there
    if (!ok) return false
  }

  // ── брендовые флаги и группы ──
  if (!flagMatches(filters.categoriesUnclear, brand.categoriesUnclear)) return false
  if (!flagMatches(filters.hasComments, brand.commentsCount > 0)) return false

  if (has(filters.categoryIds) || has(ex?.categoryIds)) {
    const own = new Set(publishedCategories(brand).map((link) => link.categoryId))
    if (has(ex?.categoryIds) && ex.categoryIds.some((id) => own.has(id))) return false
    if (has(filters.categoryIds)) {
      const ok = filters.categoriesMode === 'all'
        ? filters.categoryIds.every((id) => own.has(id))
        : filters.categoryIds.some((id) => own.has(id))
      if (!ok) return false
    }
  }

  if (filters.changedWithinDays !== undefined) {
    const edge = Date.now() - filters.changedWithinDays * 86_400_000
    if (lastChangedAt(brand) < edge) return false
  }

  if (has(filters.changedBy) || has(ex?.changedBy)) {
    const author = lastEditorId(brand)
    if (has(ex?.changedBy) && ex.changedBy.includes(author)) return false
    if (has(filters.changedBy) && !filters.changedBy.includes(author)) return false
  }

  // ── комментарии и задачи ──
  if (has(filters.commentBy)) {
    const author = brand.lastComment?.authorId ?? null
    if (author === null || !filters.commentBy.includes(author)) return false
  }
  if (filters.commentWithinDays !== undefined) {
    const at = brand.lastComment?.at
    const edge = Date.now() - filters.commentWithinDays * 86_400_000
    if (!at || new Date(at).getTime() < edge) return false
  }
  if (filters.hasTask !== undefined) {
    if (!flagMatches(filters.hasTask, brand.openTask !== null)) return false
  }

  // ── сравнение каналов ──
  if (!flagMatches(filters.channelDiff, hasStrictChannelDiff(brand, ctx))) return false
  if (!flagMatches(filters.onlyOneChannel, filledInOneChannelOnly(brand, ctx))) return false

  // ── черновик и линза ──
  if (filters.onlyMine !== undefined) {
    const mine = ctx?.draftBrandIds?.has(brand.id) ?? false
    if (!flagMatches(filters.onlyMine, mine)) return false
  }
  /* Линза работает только когда известен дифф с прошлого визита: иначе старая
     ссылка с `lens=1` показала бы пустую таблицу вместо данных. */
  if (filters.lensVisit !== undefined && ctx?.visitChangedIds) {
    if (!flagMatches(filters.lensVisit, ctx.visitChangedIds.has(brand.id))) return false
  }

  // ── составные группы представлений ──
  if (filters.todo !== undefined) {
    const todo = channelValue(brand, channel, 'url', ctx) === null || brand.categoriesUnclear
    if (!flagMatches(filters.todo, todo)) return false
  }
  if (filters.myMissing !== undefined) {
    const missing = missingCount(brand, channel, ctx) > 0
    if (!flagMatches(filters.myMissing, missing)) return false
  }

  return customFieldsMatch(brand, filters, channel, ctx)
}

/**
 * Набор строк. Бренды, созданные в черновике, остаются видны вне фильтра
 * (§4.3): человек создал бренд — он не должен исчезнуть.
 */
export function applyFilters(
  brands: readonly Brand[], filters: BrandFilters, channel: ChannelCode, ctx?: FilterContext,
): Brand[] {
  const newIds = ctx?.newBrandIds
  return brands.filter(
    (brand) => (newIds?.has(brand.id) ?? false) || matchesFilters(brand, filters, channel, ctx),
  )
}

// ───────────────────────────── сортировка (§10.3) ────────────────────────────

/** Ключ сортировки: пусто отмечается отдельно — пустые всегда в конце. */
interface SortKey {
  empty: boolean
  num: number | null
  str: string | null
}

const EMPTY_KEY: SortKey = { empty: true, num: null, str: null }

function enumRank(
  value: string | null, field: ChannelField, ctx?: FilterContext,
): SortKey {
  if (value === null) return EMPTY_KEY
  const set = ENUM_SET[field]
  const order = set ? ctx?.enumOrder?.get(set)?.get(value) : undefined
  /* Без справочника порядок неизвестен: сравниваем коды, чтобы не упасть. */
  return order === undefined ? { empty: false, num: null, str: value } : { empty: false, num: order, str: null }
}

/** Число — всегда значение, а не пусто: «0 категорий» — такой же ответ (П3). */
function numberKey(value: number): SortKey {
  return { empty: false, num: value, str: null }
}

function fieldSortKey(brand: Brand, def: FieldDefinition, channel: ChannelCode): SortKey {
  const values = def.scope === 'brand' ? [brand.fields[def.code]]
    : def.scope === 'brand_channel' ? [brand.channels[channel]?.fields[def.code]]
      : publishedCategories(brand).map((link) => link.fields[def.code])
  const value = values.find((item) => filled(item))
  if (value === undefined) return EMPTY_KEY
  if (typeof value === 'number') return { empty: false, num: value, str: null }
  if (typeof value === 'boolean') return { empty: false, num: value ? 1 : 0, str: null }
  if (typeof value === 'string') {
    if (def.type === 'date') {
      const at = Date.parse(value)
      return Number.isNaN(at) ? { empty: false, num: null, str: value } : { empty: false, num: at, str: null }
    }
    return { empty: false, num: null, str: value }
  }
  if (Array.isArray(value)) return { empty: false, num: null, str: value.join(', ') }
  return EMPTY_KEY
}

/** Значение колонки для сортировки. Неизвестная колонка — пусто, без падения. */
function sortKey(
  brand: Brand, column: string, channel: ChannelCode, ctx?: FilterContext,
): SortKey {
  if (column === 'name' || column === 'brand') return { empty: false, num: null, str: brand.name }

  if (column === 'present') {
    const here = isPresent(brand, channel, ctx)
    const there = isPresent(brand, OTHER[channel], ctx)
    return numberKey(here && there ? 0 : here ? 1 : there ? 2 : 3)
  }
  if (column === 'cats' || column === 'categories' || column === 'categoriesCount') {
    return numberKey(publishedCategories(brand).length)
  }
  if (column === 'comments') return numberKey(brand.commentsCount)
  if (column === 'changed' || column === 'updatedAt') {
    const at = lastChangedAt(brand)
    return at === 0 ? EMPTY_KEY : { empty: false, num: at, str: null }
  }
  if (column === 'missing') return numberKey(missingCount(brand, channel, ctx))
  if (column === 'unclear' || column === 'categoriesUnclear') return numberKey(brand.categoriesUnclear ? 1 : 0)
  if (column === 'diff') {
    return numberKey(hasStrictChannelDiff(brand, ctx) ? 1 : 0)
  }

  if (column.startsWith('field:')) {
    const code = column.slice('field:'.length)
    const def = ctx?.fields?.find((item) => item.code === code)
    return def ? fieldSortKey(brand, def, channel) : EMPTY_KEY
  }

  if ((CHANNEL_FIELDS as readonly string[]).includes(column)) {
    const field = column as ChannelField
    const value = channelValue(brand, channel, field, ctx)
    if (ENUM_SET[field]) return enumRank(value, field, ctx)
    return value === null ? EMPTY_KEY : { empty: false, num: null, str: value }
  }

  return EMPTY_KEY
}

function compareKeys(a: SortKey, b: SortKey, direction: 1 | -1): number {
  /* Пустые ВСЕГДА в конце, независимо от направления (§10.3). */
  if (a.empty && b.empty) return 0
  if (a.empty) return 1
  if (b.empty) return -1
  if (a.num !== null && b.num !== null) return (a.num - b.num) * direction
  const left = a.str ?? String(a.num ?? '')
  const right = b.str ?? String(b.num ?? '')
  // Тот же порядок алфавитов, что и у названий: латиница раньше кириллицы.
  return compareNames(left, right) * direction
}

/**
 * Многоуровневая сортировка (§10.3). Последний уровень — всегда алфавит
 * А→Я: два бренда с одинаковым тиром обязаны идти в предсказуемом порядке.
 */
export function sortBrands(
  brands: readonly Brand[], sort: readonly SortRule[], channel: ChannelCode, ctx?: FilterContext,
): Brand[] {
  const rules = sort.filter((rule) => rule.column !== '')
  const out = [...brands]

  out.sort((a, b) => {
    for (const rule of rules) {
      const direction = rule.direction === 'desc' ? -1 : 1
      const result = compareKeys(
        sortKey(a, rule.column, channel, ctx),
        sortKey(b, rule.column, channel, ctx),
        direction,
      )
      if (result !== 0) return result
    }
    return compareNames(a.name, b.name)
  })

  return out
}

// ───────────────────────────── счётчики чипов (§4.5) ─────────────────────────

export interface ChipCounts {
  /** Вселенная канала: 230 в рознице, 186 в опте (П7). */
  channelTotal: number
  /** Вселенная бренда: все бренды среза, в обоих каналах. */
  brandTotal: number
  /** Тир → сколько брендов, `''` — без тира. */
  tiers: Record<string, number>
  visibility: Record<string, number>
  seoPriority: Record<string, number>
  contextPriority: Record<string, number>
  stockDisplay: Record<string, number>
  oos: Record<string, number>
  /** Категория (id справочника) → сколько брендов привязано. */
  categories: Record<number, number>
  /** Автор последнего изменения → сколько брендов; 0 — импорт. */
  authors: Record<number, number>
  /** Автор последнего комментария → сколько брендов. */
  commentAuthors: Record<number, number>
  /** Брендов с незакрытой задачей. */
  tasks: number
  /** Период (дней) → у скольких брендов комментарий не старше. */
  commented: Record<number, number>
  noUrl: number
  hasUrl: number
  unclear: number
  hidden: number
  comments: number
  whiteGlove: Record<WhiteGloveField, number>
  presentIn: { retail: number; b2b: number; both: number; only_one: number }
  status: { active: number; archived: number }
  channelDiff: number
  onlyOneChannel: number
  mine: number
  lens: number
  todo: number
  myMissing: number
  /** Изменён за N дней: 7 и 30 (§10.2). */
  changed: Record<number, number>
}

function bump(map: Record<string, number>, code: string): void {
  map[code] = (map[code] ?? 0) + 1
}

function bumpId(map: Record<number, number>, id: number): void {
  map[id] = (map[id] ?? 0) + 1
}

/** Периоды чипа «Изменён за N дней» (§10.2). */
export const CHANGED_PERIODS = [7, 30] as const

/**
 * Счётчики чипов.
 *
 * Считаются по ВСЕЙ вселенной своего признака, а не по текущему
 * отфильтрованному набору: у чипа «A+ 19» рядом написано «из 230», и число,
 * которое меняется от других включённых чипов, этой подписи противоречило бы.
 * Так же ведут себя счётчики в спеке (§4.1: `Тир [A+ 19][A 23][B 15][C 26][— 147]`
 * в сумме дают 230 — весь канал). Канальные признаки считаются по брендам
 * активного канала, брендовые — по всем брендам среза (П7, §10.2).
 *
 * `brands` приходит уже суженным по статусу (активные / архив / все): статус
 * задаёт вселенную, остальные фильтры — нет.
 */
export function countChips(
  brands: readonly Brand[], channel: ChannelCode, ctx?: FilterContext,
): ChipCounts {
  const counts: ChipCounts = {
    channelTotal: 0,
    brandTotal: brands.length,
    tiers: {},
    visibility: {},
    seoPriority: {},
    contextPriority: {},
    stockDisplay: {},
    oos: {},
    categories: {},
    authors: {},
    commentAuthors: {},
    tasks: 0,
    commented: {},
    noUrl: 0,
    hasUrl: 0,
    unclear: 0,
    hidden: 0,
    comments: 0,
    whiteGlove: { delivery: 0, installation: 0, warranty: 0 },
    presentIn: { retail: 0, b2b: 0, both: 0, only_one: 0 },
    status: { active: 0, archived: 0 },
    channelDiff: 0,
    onlyOneChannel: 0,
    mine: 0,
    lens: 0,
    todo: 0,
    myMissing: 0,
    changed: {},
  }

  const now = Date.now()
  const edges = CHANGED_PERIODS.map((days) => ({ days, edge: now - days * 86_400_000 }))

  for (const brand of brands) {
    // ── вселенная бренда ──
    if (brand.status === 'archived') counts.status.archived += 1
    else counts.status.active += 1

    const here = brand.channels.retail?.isPresent === true
    const there = brand.channels.b2b?.isPresent === true
    if (here) counts.presentIn.retail += 1
    if (there) counts.presentIn.b2b += 1
    if (here && there) counts.presentIn.both += 1
    if (here !== there) counts.presentIn.only_one += 1

    if (brand.categoriesUnclear) counts.unclear += 1
    if (brand.commentsCount > 0) counts.comments += 1
    for (const link of publishedCategories(brand)) bumpId(counts.categories, link.categoryId)
    bumpId(counts.authors, lastEditorId(brand))
    if (brand.lastComment?.authorId) bumpId(counts.commentAuthors, brand.lastComment.authorId)
    if (brand.openTask) counts.tasks += 1
    const commentedAt = brand.lastComment ? new Date(brand.lastComment.at).getTime() : 0
    for (const { days, edge } of edges) {
      if (commentedAt >= edge) counts.commented[days] = (counts.commented[days] ?? 0) + 1
    }

    const changedAt = lastChangedAt(brand)
    for (const { days, edge } of edges) {
      if (changedAt >= edge) counts.changed[days] = (counts.changed[days] ?? 0) + 1
    }

    if (ctx?.draftBrandIds?.has(brand.id)) counts.mine += 1
    if (ctx?.visitChangedIds?.has(brand.id)) counts.lens += 1
    if (hasStrictChannelDiff(brand)) counts.channelDiff += 1
    if (filledInOneChannelOnly(brand)) counts.onlyOneChannel += 1

    // ── вселенная канала ──
    if (!isPresent(brand, channel)) continue
    counts.channelTotal += 1

    bump(counts.tiers, key(channelValue(brand, channel, 'tier')))
    bump(counts.visibility, key(channelValue(brand, channel, 'visibility')))
    bump(counts.seoPriority, key(channelValue(brand, channel, 'seoPriority')))
    bump(counts.contextPriority, key(channelValue(brand, channel, 'contextPriority')))
    bump(counts.stockDisplay, key(channelValue(brand, channel, 'stockDisplay')))
    bump(counts.oos, key(channelValue(brand, channel, 'oos')))

    const url = channelValue(brand, channel, 'url')
    if (url === null) counts.noUrl += 1
    else counts.hasUrl += 1
    if (channelValue(brand, channel, 'visibility') === 'no') counts.hidden += 1
    for (const field of ['delivery', 'installation', 'warranty'] as const) {
      if (channelValue(brand, channel, field) !== null) counts.whiteGlove[field] += 1
    }
    if (url === null || brand.categoriesUnclear) counts.todo += 1
    if (missingCount(brand, channel, ctx) > 0) counts.myMissing += 1
  }

  return counts
}

// ───────────────────────────── правка фильтра (§10.1) ────────────────────────

/**
 * Намерение клика по чипу, как его описывает §10.1:
 *  · `toggle`  — обычный клик: включить, повторный — снять;
 *  · `only`    — Shift+клик: исключительный выбор внутри группы;
 *  · `exclude` — Alt/Option+клик: исключение значения.
 *
 * Внимание: `<Chip>` называет те же три намерения `replace` / `add` /
 * `exclude`. Раскладка клавиш в компоненте и в §10.1 совпадает (клик / Shift /
 * Alt), а смысл первых двух назван по-разному; законом считается §10.1,
 * поэтому владелец фильтров отображает `replace → toggle`, `add → only`.
 */
export type FilterIntent = 'toggle' | 'only' | 'exclude'

export type ValueGroupKey =
  | 'tiers' | 'visibility' | 'seoPriority' | 'contextPriority' | 'stockDisplay' | 'oos'
export type NumberGroupKey = 'categoryIds' | 'changedBy' | 'commentBy'
export type BooleanFilterKey =
  | 'hasUrl' | 'categoriesUnclear' | 'hasComments' | 'hasTask'
  | 'channelDiff' | 'onlyOneChannel' | 'onlyMine' | 'lensVisit' | 'todo' | 'myMissing'

/** Состояние чипа: выключен · включён · исключён. */
export type ChipState = 'off' | 'on' | 'excluded'

function listWithout<T>(list: readonly T[] | undefined, value: T): T[] {
  return (list ?? []).filter((item) => item !== value)
}

function pruneExclusions(filters: BrandFilters): BrandFilters {
  const exclude = filters.exclude
  if (!exclude) return filters
  const cleaned: FilterExclusions = {}
  for (const [key, value] of Object.entries(exclude)) {
    if (Array.isArray(value) && value.length > 0) {
      (cleaned as Record<string, unknown>)[key] = value
    }
  }
  const next = { ...filters }
  if (Object.keys(cleaned).length === 0) delete next.exclude
  else next.exclude = cleaned
  return next
}

/** Состояние чипа значения группы. */
export function groupChipState(
  filters: BrandFilters, group: ValueGroupKey, value: string | null,
): ChipState {
  const current = key(value)
  if ((filters.exclude?.[group] ?? []).some((item) => key(item) === current)) return 'excluded'
  if ((filters[group] ?? []).some((item) => key(item) === current)) return 'on'
  return 'off'
}

/** Новый фильтр после клика по чипу значения (§10.1). */
export function withGroupValue(
  filters: BrandFilters, group: ValueGroupKey, value: string | null, intent: FilterIntent,
): BrandFilters {
  const current = key(value)
  const include = (filters[group] ?? []).map(key)
  const exclude = (filters.exclude?.[group] ?? []).map(key)
  const next: BrandFilters = { ...filters, exclude: { ...filters.exclude } }

  if (intent === 'exclude') {
    const nextExclude = exclude.includes(current)
      ? listWithout(exclude, current)
      : [...exclude, current]
    next[group] = listWithout(include, current)
    next.exclude = { ...next.exclude, [group]: nextExclude }
  } else if (intent === 'only') {
    next[group] = [current]
    next.exclude = { ...next.exclude, [group]: listWithout(exclude, current) }
  } else {
    next[group] = include.includes(current)
      ? listWithout(include, current)
      : [...include, current]
    next.exclude = { ...next.exclude, [group]: listWithout(exclude, current) }
  }

  if ((next[group] ?? []).length === 0) delete next[group]
  return pruneExclusions(next)
}

/** Состояние чипа для числовой группы: категории, авторы. */
export function numberChipState(
  filters: BrandFilters, group: NumberGroupKey, id: number,
): ChipState {
  if ((filters.exclude?.[group] ?? []).includes(id)) return 'excluded'
  if ((filters[group] ?? []).includes(id)) return 'on'
  return 'off'
}

/** Новый фильтр после клика по чипу числовой группы (категория, автор). */
export function withNumberValue(
  filters: BrandFilters, group: NumberGroupKey, id: number, intent: FilterIntent,
): BrandFilters {
  const include = filters[group] ?? []
  const exclude = filters.exclude?.[group] ?? []
  const next: BrandFilters = { ...filters, exclude: { ...filters.exclude } }

  if (intent === 'exclude') {
    next[group] = listWithout(include, id)
    next.exclude = {
      ...next.exclude,
      [group]: exclude.includes(id) ? listWithout(exclude, id) : [...exclude, id],
    }
  } else if (intent === 'only') {
    next[group] = [id]
    next.exclude = { ...next.exclude, [group]: listWithout(exclude, id) }
  } else {
    next[group] = include.includes(id) ? listWithout(include, id) : [...include, id]
    next.exclude = { ...next.exclude, [group]: listWithout(exclude, id) }
  }

  if ((next[group] ?? []).length === 0) delete next[group]
  return pruneExclusions(next)
}

/**
 * Состояние чипа-тумблера. Исключение тумблера — это обратное значение
 * признака: «− Без URL» и «URL заполнен» — одно и то же условие, поэтому
 * отдельного места для исключений флагам не нужно.
 */
export function flagChipState(
  filters: BrandFilters, flag: BooleanFilterKey, on: boolean,
): ChipState {
  const value = filters[flag]
  if (value === undefined) return 'off'
  return value === on ? 'on' : 'excluded'
}

/** Новый фильтр после клика по чипу-тумблеру (§10.1). */
export function withFlag(
  filters: BrandFilters, flag: BooleanFilterKey, on: boolean, intent: FilterIntent,
): BrandFilters {
  const state = flagChipState(filters, flag, on)
  const next: BrandFilters = { ...filters }

  if (intent === 'exclude') {
    if (state === 'excluded') delete next[flag]
    else next[flag] = !on
    return next
  }
  /* У тумблера «исключительный выбор внутри группы» совпадает с включением:
     в группе одно значение. */
  if (state === 'on') delete next[flag]
  else next[flag] = on
  return next
}

/** Группа фильтра → набор справочника: подписи значений и их порядок. */
export const GROUP_ENUM_SET: Record<ValueGroupKey, EnumSetCode> = {
  tiers: 'tier',
  visibility: 'visibility',
  seoPriority: 'seo_priority',
  contextPriority: 'context_priority',
  stockDisplay: 'stock_display',
  oos: 'oos',
}

/**
 * Значение признака, при котором чип-тумблер считается «включённым».
 * «Без URL» включён, когда адреса нет, поэтому у него `false`.
 */
export const FLAG_ON: Record<BooleanFilterKey, boolean> = {
  hasUrl: false,
  categoriesUnclear: true,
  hasComments: true,
  hasTask: true,
  channelDiff: true,
  onlyOneChannel: true,
  onlyMine: true,
  lensVisit: true,
  todo: true,
  myMissing: true,
}
